/**
 * Bulk-import spec for the `Users` sheet of the organizer workbook.
 *
 * The Users sheet creates global identity records via `createContact`. Each
 * row becomes a `contacts` doc (or matches an existing one — the callable
 * dedupes by email). Role defaults to `invitee` when blank; honorifics +
 * profile/logistics fields are passthrough strings; firstName + lastName
 * fall back to a single `displayName` split when the explicit fields are absent.
 *
 * One column-key per Contact field — keep this in lockstep with the
 * workbook generator (see `scripts/generate-organizer-templates.mjs`); the
 * round-trip test catches drift on every CI run.
 */
import type { Role } from "../types/person";
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import {
  ASSIGNABLE_ROLES,
  DEFAULT_IMPORT_ROLE,
  EMAIL_PATTERN,
  normalize,
} from "./importTypes";

export interface RawUserImportRow extends Record<string, string | undefined> {
  email?: string;
  firstName?: string;
  lastName?: string;
  honorificPrefix?: string;
  honorificSuffix?: string;
  role?: string;
  title?: string;
  organization?: string;
  phone?: string;
  workCity?: string;
  workState?: string;
  bio?: string;
  linkedIn?: string;
  website?: string;
  photoURL?: string;
  dietaryRestrictions?: string;
  accessibilityNeeds?: string;
  internalNotes?: string;
  internalTags?: string;
}

/** Parsed payload — matches the createContact callable's input shape. */
export interface ParsedUserImportRow {
  email: string;
  firstName: string | null;
  lastName: string | null;
  honorificPrefix: string | null;
  honorificSuffix: string | null;
  /** Computed from name parts for display in the preview table. */
  displayName: string | null;
  role: Role;
  title: string | null;
  organization: string | null;
  phone: string | null;
  workCity: string | null;
  workState: string | null;
  bio: string | null;
  linkedIn: string | null;
  website: string | null;
  photoURL: string | null;
  dietaryRestrictions: string[];
  accessibilityNeeds: string | null;
  internalNotes: string | null;
  internalTags: string[];
}

function buildDisplayName(
  firstName: string,
  lastName: string,
  honorificPrefix: string | null,
  honorificSuffix: string | null,
): string | null {
  const parts = [honorificPrefix, firstName, lastName].filter(Boolean);
  if (parts.length === 0) return null;
  const base = parts.join(" ");
  return honorificSuffix ? `${base}, ${honorificSuffix}` : base;
}

function parseList(value: string): string[] {
  return value
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export const userImportSpec: ImportSpec<RawUserImportRow, ParsedUserImportRow> =
  {
    sheetName: "Users",
    purpose:
      "People in the system. Upload via Users page → Bulk Import. Match these columns exactly.",
    columns: [
      { key: "email", note: "required — the person's email address" },
      { key: "firstName", note: "optional but recommended" },
      { key: "lastName", note: "optional but recommended" },
      { key: "honorificPrefix", note: "optional — Dr., Prof., Hon., etc." },
      { key: "honorificSuffix", note: "optional — PhD, Esq., MD, etc." },
      {
        key: "role",
        note: "one of: invitee | companion | internal | organizer | admin (defaults to invitee)",
      },
      { key: "title", note: "optional — job title" },
      { key: "organization", note: "optional — company / firm" },
      { key: "phone", note: "optional — mobile or work phone" },
      { key: "workCity", note: "optional — professional city" },
      { key: "workState", note: "optional — professional state / region" },
      { key: "bio", note: "optional — free-text bio shown in directory if privacy allows" },
      { key: "linkedIn", note: "optional — LinkedIn URL or handle" },
      { key: "website", note: "optional — personal or firm website URL" },
      {
        key: "photoURL",
        note: "optional — HTTPS URL to a profile photo (https://… only)",
      },
      {
        key: "dietaryRestrictions",
        note: "optional — comma/semicolon-separated dietary notes",
      },
      {
        key: "accessibilityNeeds",
        note: "optional — accessibility or mobility needs",
      },
      {
        key: "internalNotes",
        note: "optional — organizer-only notes, not visible to attendees",
      },
      {
        key: "internalTags",
        note: "optional — comma/semicolon-separated organizer tags (e.g. vip, speaker, press)",
      },
    ],
    example: {
      email: "alice.park@example.com",
      firstName: "Alice",
      lastName: "Park",
      honorificPrefix: "",
      honorificSuffix: "",
      role: "invitee",
      title: "Portfolio Manager",
      organization: "Apex Capital",
      phone: "+1-555-0100",
      workCity: "New York",
      workState: "NY",
      bio: "20 years in private credit. Speaks on US opportunities and CLO structuring.",
      linkedIn: "https://linkedin.com/in/alice-park",
      website: "https://apexcapital.com/team/alice-park",
      photoURL: "",
      dietaryRestrictions: "vegetarian",
      accessibilityNeeds: "",
      internalNotes: "",
      internalTags: "vip, speaker",
    },
    validate(raw, lineNumber) {
      const email = normalize(raw.email).toLowerCase();
      const firstName = normalize(raw.firstName);
      const lastName = normalize(raw.lastName);
      const honorificPrefix = normalize(raw.honorificPrefix) || null;
      const honorificSuffix = normalize(raw.honorificSuffix) || null;
      const roleRaw = normalize(raw.role).toLowerCase();
      const title = normalize(raw.title) || null;
      const organization = normalize(raw.organization) || null;
      const phone = normalize(raw.phone) || null;
      const workCity = normalize(raw.workCity) || null;
      const workState = normalize(raw.workState) || null;
      const bio = normalize(raw.bio) || null;
      const linkedIn = normalize(raw.linkedIn) || null;
      const website = normalize(raw.website) || null;
      const photoURL = normalize(raw.photoURL) || null;
      const dietaryRestrictions = parseList(normalize(raw.dietaryRestrictions));
      const accessibilityNeeds = normalize(raw.accessibilityNeeds) || null;
      const internalNotes = normalize(raw.internalNotes) || null;
      const internalTags = parseList(normalize(raw.internalTags));

      const displayName = buildDisplayName(
        firstName,
        lastName,
        honorificPrefix,
        honorificSuffix,
      );

      const base: Omit<ParsedRow<ParsedUserImportRow>, "status" | "message"> = {
        lineNumber,
        data: {
          email,
          firstName: firstName || null,
          lastName: lastName || null,
          honorificPrefix,
          honorificSuffix,
          displayName,
          role: DEFAULT_IMPORT_ROLE,
          title,
          organization,
          phone,
          workCity,
          workState,
          bio,
          linkedIn,
          website,
          photoURL,
          dietaryRestrictions,
          accessibilityNeeds,
          internalNotes,
          internalTags,
        },
      };

      if (!email) {
        return invalid(base, "Email is required");
      }
      if (!EMAIL_PATTERN.test(email)) {
        return invalid(base, "Invalid email format");
      }
      if (photoURL && !/^https:\/\//i.test(photoURL)) {
        return invalid(base, "photoURL must be an HTTPS URL");
      }

      let role: Role = DEFAULT_IMPORT_ROLE;
      if (roleRaw) {
        if (!(ASSIGNABLE_ROLES as readonly string[]).includes(roleRaw)) {
          return invalid(
            base,
            `Unknown role "${roleRaw}". Must be one of: ${ASSIGNABLE_ROLES.join(", ")}`,
          );
        }
        role = roleRaw as Role;
      }

      return pending({
        ...base,
        data: { ...base.data, role },
      });
    },
    dedupe(rows) {
      const seen = new Set<string>();
      return rows.map((row) => {
        if (row.status !== "pending") return row;
        if (seen.has(row.data.email)) {
          return {
            ...row,
            status: "invalid" as const,
            message: "Duplicate email within input",
          };
        }
        seen.add(row.data.email);
        return row;
      });
    },
  };
